import { handlelize } from './utils-handlelize.js';
import { urlPath } from './utils-url-path.js';

class AddOnVariantSelect extends HTMLElement {
  abortController;

  /**
   * Connected callback.
   */
  connectedCallback() {
    this.initOptions();
  }

  /**
   * Disconnected callback.
   */
  disconnectedCallback() {
    this.abortController = null;
    this.removeEventListener('change', this.changeHandler);
    this.removeEventListener('keydown', this.keydownHandler);
  }

  /**
   * Initialize the options.
   */
  initOptions() {
    this.options = this.querySelectorAll('[data-add-on-option]');
    this.sectionId = this.dataset.sectionId;
    this.urlParam = this.dataset.urlParam;
    this.disableBasedOnVariantAvailability =
      this.dataset.disableBasedOnVariantAvailability === 'true';
    this.variantsJson = JSON.parse(this.querySelector('[data-add-on-variants-json]').textContent);
    this.possibleProductFormParams = this.getProductFormPossibleParams();

    // Handle change, trigger onVariantChange
    this.changeHandler = (event) => {
      const target = event.target.closest('[data-add-on-option]');
      if (target && !this.abortController) this.onVariantChange({ target });
    };
    this.addEventListener('change', this.changeHandler);

    // Handle keydown, trigger onVariantChange
    this.keydownHandler = (event) => {
      if (event.key !== 'Enter') return;
      const target = event.target.closest('[data-add-on-option]');
      if (target && !this.abortController) {
        event.preventDefault();
        target.checked = true;
        this.onVariantChange({ target });
      }
    };
    this.addEventListener('keydown', this.keydownHandler);

    // Init options based on the URL params
    this.init();
  }

  /**
   * Get the possible product form params to reconstruct the URL later.
   */
  getProductFormPossibleParams() {
    const parentForm = this.closest('product-form');
    if (!parentForm) return { bundleParams: [], addOnParams: [] };

    const bundleVariantSelects = Array.from(parentForm.querySelectorAll('bundle-variant-select'));
    const addOnVariantSelects = Array.from(parentForm.querySelectorAll('add-on-variant-select'));

    const bundleParams = bundleVariantSelects.map(
      (variantSelect) => variantSelect.dataset.urlParam
    );
    const addOnParams = addOnVariantSelects.map((variantSelect) => variantSelect.dataset.urlParam);

    return { bundleParams, addOnParams };
  }

  /**
   * Initialize the add-on variant select based on the URL params
   */
  init() {
    const params = new URLSearchParams(window.location.search);
    const urlParam = params.get(this.urlParam);

    // If there is a URL param, set the options in the UI
    if (urlParam) {
      const options = urlParam.split('_');
      this.options.forEach((option) => {
        if (option.dataset?.hidden) {
          return;
        }
        const value = handlelize(option.value);
        if (options.includes(value)) {
          option.checked = true;
          if (typeof option.closest('product-option')?.changeHandler === 'function') {
            option.closest('product-option')?.changeHandler();
          }
        } else {
          option.checked = false;
        }
      });
    }

    // Each fieldset should have one option checked
    this.querySelectorAll('fieldset').forEach((fieldset) => {
      fieldset.querySelectorAll('[data-add-on-option]').forEach((option) => {
        const checkedOption = fieldset.querySelector('[data-add-on-option]:checked');
        if (!checkedOption && !option.dataset?.hidden) {
          option.checked = true;
          if (typeof option.closest('product-option')?.changeHandler === 'function') {
            option.closest('product-option')?.changeHandler();
          }
        }
      });
    });

    const selectedOptions = [...this.querySelectorAll('[data-add-on-option]:checked')];

    // Trigger for after page load
    // Delay so it always triggered after a variant change
    if (selectedOptions.length > 0) {
      setTimeout(() => {
        selectedOptions.forEach((option) => {
          this.onVariantChange({ target: option });
        });
      });
    }
  }

  /**
   * Get the selected variant
   * Match the variant id from the variantsJson
   * @param {Event} event - The event object.
   */
  getSelectedVariant({ target }) {
    // Return if there is no variantJson
    if (!this.variantsJson) return;

    // Get the options that are checked
    const options = [...this.options]
      .filter((option) => option.checked)
      .map((option) => ({
        updated: option.value === target.value,
        option: option.name,
        value: option.value,
      }));

    // First match the updated option from the target
    let variants = this.variantsJson.filter((variant) => variant.options.includes(target.value));

    // Then if we have multiple variants, filter further using the first other option
    if (variants.length > 1) {
      const variants2 = variants.filter((variant) => {
        const value = options.find((option) => !option.updated)?.value;
        return variant.options.includes(value);
      });
      variants = variants2.length === 0 ? variants : variants2;
    }

    // Then if we have multiple variants, filter further using the last other option
    if (variants.length > 1) {
      const variants3 = variants.filter((variant) => {
        const value = options.findLast((option) => !option.updated)?.value;
        return variant.options.includes(value);
      });
      variants = variants3.length === 0 ? variants : variants3;
    }

    return variants[0];
  }

  /**
   * Handle variant change.
   * @param {Event} event - The event object.
   */
  onVariantChange(event) {
    const target = event.target;

    // Get the preselect values
    const preselectValues = [...this.querySelectorAll('[data-preselect-option]')].map((option) =>
      handlelize(option.value)
    );

    // Check if any none option is selected
    const noneOptionSelected = [...this.querySelectorAll(`[data-none-option]`)].some(
      (option) => option.checked
    );

    // Check if the first option is selected
    // This is used to determine if the URL should be updated if the none option is selected
    // Keeping in mind that we must ignore hidden inputs.
    const nearestFieldSet = target.closest('fieldset');
    const firstVisibleOption = nearestFieldSet?.querySelector(
      '[data-add-on-option]:not([data-hidden])'
    );
    const firstOptionIsSelected = firstVisibleOption ? firstVisibleOption.checked : true;

    // Find to selected variant
    // Note: Variant is not always available for every option combination
    const variant = this.getSelectedVariant(event);

    // Get the current option values (if no variant use the input values)
    const optionValues =
      variant?.options ||
      [...this.querySelectorAll(`[data-add-on-option]`)]
        .map((option) => option.checked && option.value)
        .filter(Boolean);

    // Immediately uncheck all other checkboxes within the same add-on product to improve perceived speed.
    this.updateVariantOptions(target, variant, optionValues, noneOptionSelected);

    // Update the URL
    this.updateURL(optionValues, preselectValues, noneOptionSelected, firstOptionIsSelected);

    // Publish the add-on variant change event
    publish(`add-on:variant:changed:${this.sectionId}`, {
      variant,
      group: this.urlParam,
      noneOption: noneOptionSelected,
    });
  }

  /**
   * Update URL
   * @param {Array} optionValues - The option values.
   * @param {Array} preselectValues - The preselect values.
   */
  updateURL(optionValues = [], preselectValues = [], noneOptionSelected = false) {
    const { searchParams } = new URL(window.location);
    if (!noneOptionSelected && optionValues.length > 0) {
      // Remove the preselect values and join the values with an underscore
      const values = optionValues
        .filter((value) => {
          const handle = handlelize(value);
          return !preselectValues.includes(handle);
        })
        .map((value) => handlelize(value))
        .join('_');
      if (values) {
        searchParams.set(this.urlParam, values);
      }
    } else {
      searchParams.delete(this.urlParam);
    }

    // Get the current path
    const path = urlPath();

    // Replace the current history state with the new search params, without reloading the page
    try {
      if (searchParams.size) {
        this.constructSearchParamsWithOrder(searchParams);
        history.replaceState(null, '', `${path}?${searchParams}`);
      } else {
        history.replaceState(null, '', path);
      }
    } catch (error) {
      console.error('Error updating URL', error);
    }
  }

  /**
   * Construct the search params with the correct order (general -> variant -> bundle -> add-on).
   * @param {URLSearchParams} originalSearchParams - The search params object to be ordered.
   */
  constructSearchParamsWithOrder(searchParams) {
    const newBundleParams = [];
    const newAddOnParams = [];

    // Check for each bundle param IN ORDER if it is in the search params.
    // We want bundle 1 -> bundle 2 -> bundle 3, etc.
    this.possibleProductFormParams.bundleParams.forEach((param) => {
      const existingParam = searchParams.get(param);
      if (existingParam) {
        newBundleParams.push({ key: param, value: existingParam });
        searchParams.delete(param);
      }
    });

    // Also do this for add-on items.
    this.possibleProductFormParams.addOnParams.forEach((param) => {
      const existingParam = searchParams.get(param);
      if (existingParam) {
        newAddOnParams.push({ key: param, value: existingParam });
        searchParams.delete(param);
      }
    });

    newBundleParams.forEach((paramObject) => {
      searchParams.set(paramObject.key, paramObject.value);
    });
    newAddOnParams.forEach((paramObject) => {
      searchParams.set(paramObject.key, paramObject.value);
    });
  }

  /**
   * Update the variant options.
   * @param {HTMLElement} target - The target element.
   * @param {Variant} variant - The variant object.
   * @param {Array} optionValues - The option values.
   */
  updateVariantOptions(target, variant, optionValues, noneOptionSelected = false) {
    if (!target || !optionValues) return;

    this.querySelectorAll('[data-add-on-option]').forEach((option) => {
      // If the option is in the same group
      if (option.dataset.addOnOption === target.dataset.addOnOption) {
        // Ensure the target option is checked and the other options in the group not
        option.checked = option === target;
      }

      // None options are always enabled
      if (option.dataset.noneOption) {
        option.classList.remove('disabled');
        return;
      }

      // If none option is selected, disable the option
      if (noneOptionSelected) {
        option.classList.remove('disabled');
        return;
      }

      // Disable the option if it's not any variant based on the variant options and the two other active options
      const disabled =
        (!variant && option.checked) ||
        !this.variantsJson.find((variant) => {
          const index = variant.options.indexOf(option.value);
          const otherOptions = variant.options.filter((value, i) => i !== index);
          const disabled =
            otherOptions.every((value) => optionValues.includes(value)) &&
            variant.options.includes(option.value) &&
            (variant.available === true || !this.disableBasedOnVariantAvailability);
          return disabled;
        });
      option.classList.toggle('disabled', disabled);
    });
  }
}

customElements.define('add-on-variant-select', AddOnVariantSelect);
